import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFluidHover } from '../hooks/useFluidHover';
import { fluidDistort } from '../utils/fluidDistort';
type Props = {
  slug: string;
  title: string;
  meta: string;
  image: string;
  tags: string[];
};

/**
 * One project tile: an image with a minimal caption directly beneath it. Cards stay in place; the photo drifts
 * inside its frame. On mouse hover the photo turns liquid around the cursor (WebGL flow field, with an SVG ripple
 * fallback if the image host blocks WebGL from reading it).
 */
export function WorkCard({
  slug,
  title,
  meta,
  image,
  tags
}: Props) {
  const ref = useRef<HTMLLIElement>(null);
  const reduce = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  // Strong drift of the photo inside its frame
  const imgY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-14%', '14%']);

  // Liquid hover — mouse only, not on phone-sized screens or with reduced motion
  const fluid = useFluidHover(reduce);
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const svgFallback = useRef(false);
  const pointerInside = useRef(false);
  const onFrameEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    if (reduce || e.pointerType !== 'mouse' || window.innerWidth < 768 || !frameRef.current || !imgRef.current) return;
    pointerInside.current = true;
    const {
      clientX,
      clientY
    } = e;
    fluidDistort().enter(frameRef.current, imgRef.current, clientX, clientY).then(ok => {
      if (ok || !pointerInside.current) return;
      svgFallback.current = true;
      fluid.onEnter(e);
    });
  };
  const onFrameMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!frameRef.current || e.pointerType !== 'mouse' || !pointerInside.current) return;
    if (svgFallback.current) fluid.onMove(e);else fluidDistort().move(frameRef.current, e.clientX, e.clientY);
  };
  const onFrameLeave = () => {
    pointerInside.current = false;
    if (frameRef.current) fluidDistort().leave(frameRef.current);
    fluid.onLeave();
  };
  return <li ref={ref} data-id="element-404" data-mp="35pvkqkb1wudm">
      <div data-id="element-405" data-mp="35qeq71jilqar">
        <Link to={`/work/${slug}`} className="group block focus:outline-none" data-id="element-406" data-mp="35p3nl51t2ol8">
          <div ref={frameRef} onPointerEnter={onFrameEnter} onPointerMove={onFrameMove} onPointerLeave={onFrameLeave} className="relative aspect-[3/2] overflow-hidden bg-fg/5 group-focus-visible:ring-2 group-focus-visible:ring-fg" data-id="element-407" data-mp="290e8fmhkca2y">
            {/* Fallback ripple (SVG): noise masked to a soft spot under the pointer, neutral grey elsewhere */}
            <svg aria-hidden="true" className="absolute h-0 w-0" data-id="element-408" data-mp="3teo9nwt9a1yq">
              <filter id={fluid.filterId} x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB" data-id="element-409" data-mp="296cmtvtlclu">
                <feTurbulence ref={fluid.turbRef} type="fractalNoise" baseFrequency="0.007 0.009" numOctaves={1} seed={3} result="noise" data-id="element-410" data-mp="1mrqmyde3jfut" />
                <feImage ref={fluid.spotRef} href={fluid.spotUrl} x="0" y="0" width="0" height="0" preserveAspectRatio="none" result="spot" data-id="element-411" data-mp="1ht5u4z16byge" />
                <feComposite in="noise" in2="spot" operator="in" result="localNoise" data-id="element-412" data-mp="182uisrf5umzh" />
                <feFlood floodColor="rgb(128,128,128)" result="neutral" data-id="element-413" data-mp="140zgpkpeo5kn" />
                <feComposite in="localNoise" in2="neutral" operator="over" result="map" data-id="element-414" data-mp="13ywfe4isyq0m" />
                <feDisplacementMap ref={fluid.dispRef} in="SourceGraphic" in2="map" scale={0} xChannelSelector="R" yChannelSelector="G" data-id="element-415" data-mp="z0l55solidhv" />
              </filter>
            </svg>
            <motion.div ref={fluid.targetRef} style={{
            y: imgY
          }} className="absolute inset-x-0 -inset-y-[18%] will-change-transform" data-id="element-416" data-mp="p50akfluwfi5">
              <img ref={imgRef} src={image} alt={`${title} project`} loading="lazy" className="h-full w-full object-cover" data-id="element-417" data-mp="2oeq8l2pikhe0" />
            </motion.div>
          </div>

          <div className="mt-4 flex items-start justify-between gap-6" data-id="element-418" data-mp="2rjqr93e95iay">
            <div className="min-w-0" data-id="element-419" data-mp="2wi1zj9owfkr2">
              <h3 className="text-[21px] leading-[1.25] tracking-[-0.01em] text-fg md:text-[24px]" data-id="element-420" data-mp="2wh0ecce65hvm">{title}</h3>
              <p className="mt-1.5 line-clamp-2 max-w-[460px] text-[15px] leading-[1.5] text-fg/50 md:text-[16px]" data-id="element-421" data-mp="2hjzronau4d40">{meta}</p>
              {tags.length > 0 && <ul className="mt-3 flex flex-wrap items-center gap-x-2 text-[12px] uppercase tracking-[0.04em] text-fg/40" data-id="element-422" data-mp="28qwl8yylx0iv">
                  {tags.map((tag, i) => <li key={tag} className="flex items-center gap-x-2" data-id="element-423" data-mp="1yu0byr0vwad9" data-mpi={i}>
                      {i > 0 && <span className="h-2.5 w-px bg-fg/25" aria-hidden="true" data-id="element-424" data-mp="1yrxczhhr76we" />}
                      {tag}
                    </li>)}
                </ul>}
            </div>
            <ArrowUpRightIcon className="mt-1 h-5 w-5 shrink-0 text-fg/50 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden="true" data-id="element-425" data-mp="1tunmdyimop6z" />
          </div>
        </Link>
      </div>
    </li>;
}